import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiExternalLink, FiFileText, FiX } from 'react-icons/fi';
import HeaderDateFilters from '../../pages/Tasks/components/Filters/HeaderDateFilters';
import api, { ItemsAPI } from '../../services/api';

function ymdFromISO(iso) {
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ''; }
}

function last7Range() {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date(Date.now() - 6 * 24 * 60 * 60 * 1000);
  start.setHours(0, 0, 0, 0);
  return { startDate: start.toISOString(), endDate: end.toISOString(), label: 'Last 7 days' };
}

function formatDelta(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  const txt = abs % 1 === 0 ? String(abs) : abs.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return `${n >= 0 ? '+' : '-'}${txt}`;
}

function formatDeltaParts(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return { sign: '+', value: '0' };
  const abs = Math.abs(n);
  const value = abs % 1 === 0 ? String(abs) : abs.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return { sign: n >= 0 ? '+' : '-', value };
}

function badgeForType(eventType) {
  const t = String(eventType || '').toUpperCase();
  if (t === 'SALE') return { bg: 'rgba(37,99,235,0.10)', fg: '#1e40af', bd: 'rgba(37,99,235,0.25)' };
  if (t.startsWith('RESERVE')) return { bg: 'rgba(245,158,11,0.12)', fg: '#92400e', bd: 'rgba(245,158,11,0.30)' };
  if (t.startsWith('RELEASE')) return { bg: 'rgba(16,185,129,0.12)', fg: '#065f46', bd: 'rgba(16,185,129,0.30)' };
  if (t === 'MANUAL_ADJUST') return { bg: 'rgba(107,114,128,0.10)', fg: '#374151', bd: 'rgba(107,114,128,0.25)' };
  if (t === 'BASELINE') return { bg: 'rgba(148,163,184,0.12)', fg: '#334155', bd: 'rgba(148,163,184,0.30)' };
  return { bg: 'rgba(107,114,128,0.10)', fg: '#374151', bd: 'rgba(107,114,128,0.20)' };
}

export default function ItemInventoryTimeline({ itemId }) {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(() => last7Range());
  const [filter, setFilter] = useState('all'); // all | sales | reserve | release | manual | baseline
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({ opening: 0, additions: 0, reductions: 0, netChange: 0, closing: 0 });
  const [pdfPanel, setPdfPanel] = useState({ open: false, title: '', url: '', sourceType: '', sourceId: null, loading: false, error: '' });
  const numericFont = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

  const params = useMemo(() => {
    const fromDate = dateRange?.startDate ? ymdFromISO(dateRange.startDate) : '';
    const toDate = dateRange?.endDate ? ymdFromISO(dateRange.endDate) : '';
    return { fromDate, toDate, page, pageSize };
  }, [dateRange, page, pageSize]);

  useEffect(() => {
    // Reset pagination when range changes
    setPage(1);
    setEvents([]);
  }, [dateRange?.startDate, dateRange?.endDate, itemId]);

  useEffect(() => {
    let alive = true;
    async function fetchPage() {
      if (!itemId) return;
      setLoading(true);
      setError('');
      try {
        const data = await ItemsAPI.timeline(itemId, params);
        if (!alive) return;
        setSummary(data?.summary || summary);
        setTotal(Number(data?.total || 0));
        const list = Array.isArray(data?.events) ? data.events : [];
        setEvents((prev) => (page === 1 ? list : [...prev, ...list]));
      } catch (e) {
        if (!alive) return;
        setError('Failed to load activity');
      } finally {
        if (alive) setLoading(false);
      }
    }
    fetchPage();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, params.fromDate, params.toDate, params.page, params.pageSize]);

  const filtered = useMemo(() => {
    const f = filter;
    if (f === 'all') return events;
    const typeMatch = (ev) => {
      const t = String(ev?.eventType || '').toUpperCase();
      if (f === 'sales') return t === 'SALE';
      if (f === 'reserve') return t.startsWith('RESERVE');
      if (f === 'release') return t.startsWith('RELEASE');
      if (f === 'manual') return t === 'MANUAL_ADJUST';
      if (f === 'baseline') return t === 'BASELINE';
      return true;
    };
    return events.filter(typeMatch);
  }, [events, filter]);

  const canLoadMore = events.length < total;

  const typeLabel = (eventType) => {
    const t = String(eventType || '').toUpperCase();
    if (t === 'SALE') return 'Sales';
    if (t.startsWith('RESERVE')) return 'Reservation';
    if (t.startsWith('RELEASE')) return 'Release';
    if (t === 'MANUAL_ADJUST') return 'Manual';
    if (t === 'BASELINE') return 'Baseline';
    return t || '-';
  };

  const openPdfSidePanel = async ({ sourceType, sourceId, title }) => {
    if (!sourceId) return;
    const st = String(sourceType || '').toUpperCase();
    let endpoint = '';
    if (st === 'INVOICE') endpoint = `/api/invoices/${sourceId}/pdf-url`;
    else if (st === 'QUOTE') endpoint = `/api/quotes/${sourceId}/pdf-url`;
    else return;

    setPdfPanel({ open: true, title: title || 'Document PDF', url: '', sourceType: st, sourceId, loading: true, error: '' });
    try {
      const { data } = await api.get(endpoint);
      const url = data?.url;
      if (!url) throw new Error('No URL returned');
      setPdfPanel((s) => ({ ...s, url, loading: false, error: '' }));
    } catch (e) {
      setPdfPanel((s) => ({ ...s, loading: false, error: 'Failed to load PDF' }));
    }
  };

  const openSource = (ev) => {
    const sourceType = String(ev?.sourceType || '').toUpperCase();
    const sourceId = ev?.sourceId;
    if (!sourceId) return;
    // Open PDFs in a side panel instead of navigating away.
    if (sourceType === 'INVOICE' || sourceType === 'QUOTE') {
      const meta = ev?.meta || {};
      const title =
        sourceType === 'INVOICE'
          ? `Invoice ${meta.invoiceNumber || sourceId}`
          : `Quote ${meta.quoteNumber || sourceId}`;
      openPdfSidePanel({ sourceType, sourceId, title });
      return;
    }
    // Orders don't have a PDF endpoint today.
    else if (sourceType === 'ORDER') navigate(`/orders/${sourceId}`);
  };

  const chip = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => setFilter(id)}
      style={{
        padding: '6px 10px',
        borderRadius: 9999,
        border: '1px solid rgba(229,231,235,1)',
        background: filter === id ? 'rgba(17,24,39,0.08)' : 'rgba(243,244,246,0.6)',
        color: filter === id ? '#111827' : '#374151',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer'
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Date range</div>
          <HeaderDateFilters
            value={dateRange}
            onChange={(v) => {
              if (v === 'all') {
                // Clear: remove date filter (no from/to)
                setDateRange({ startDate: '', endDate: '', label: 'All time' });
                return;
              }
              setDateRange(v);
            }}
          />
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32 }}
          onClick={() => { setPage(1); setEvents([]); }}
          disabled={loading}
          title="Refresh"
        >
          Refresh
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(120px, 1fr))', gap: 8, marginBottom: 10 }}>
        {[
          ['Opening', summary.opening],
          ['Added', summary.additions],
          ['Removed', summary.reductions],
          ['Net', summary.netChange],
          ['Closing', summary.closing]
        ].map(([k, v]) => (
          <div key={k} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 10, background: '#fff' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', fontWeight: 600 }}>{k}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginTop: 4 }}>{Number(v || 0)}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        {chip('all', 'All')}
        {chip('sales', 'Sales')}
        {chip('reserve', 'Reservations')}
        {chip('release', 'Releases')}
        {chip('manual', 'Manual')}
        {chip('baseline', 'Baseline')}
      </div>

      {error && (
        <div style={{ fontSize: 12, color: '#991b1b', border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(254,242,242,0.9)', padding: '8px 10px', borderRadius: 10, marginBottom: 10 }}>
          {error}
        </div>
      )}

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 920, borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 160 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 180 }} />
              <col style={{ width: 160 }} />
              {/* Source: takes remaining width */}
              <col />
              <col style={{ width: 90 }} />
              <col style={{ width: 110 }} />
            </colgroup>
            <thead>
              <tr>
                {['Date', 'Type', 'Customer', 'Updated by', 'Source'].map((h) => (
                  <th
                    key={h}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb',
                      padding: '10px 12px',
                      fontSize: 11,
                      color: '#6b7280',
                      fontWeight: 700,
                      textAlign: 'left',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {h}
                  </th>
                ))}
                <th
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '10px 12px',
                    fontSize: 11,
                    color: '#6b7280',
                    fontWeight: 700,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    fontFamily: numericFont,
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  Δ
                </th>
                <th
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    background: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    padding: '10px 12px',
                    fontSize: 11,
                    color: '#6b7280',
                    fontWeight: 700,
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    fontFamily: numericFont,
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  Balance
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} style={{ padding: 14, fontSize: 12, color: '#6b7280' }}>
                    No activity in this range.
                  </td>
                </tr>
              )}

              {filtered.map((ev) => {
                const badge = badgeForType(ev.eventType);
                const occurred = ev.occurredAt ? new Date(ev.occurredAt) : null;
                const dateTxt = occurred && !isNaN(occurred) ? occurred.toLocaleString() : '-';
                const meta = ev.meta || {};
                const sourceType = String(ev.sourceType || '').toUpperCase();
                const cust = meta.customerName || meta.customerId || '-';
                const byUser = ev.createdByUser
                  ? `${ev.createdByUser.firstName || ''} ${ev.createdByUser.lastName || ''}`.trim() || ev.createdByUser.email
                  : (ev.createdByUserId || '-');
                const sourceLabel =
                  sourceType === 'INVOICE'
                    ? `Invoice ${meta.invoiceNumber || ev.sourceId || ''}${meta.createdFrom ? ` (${meta.createdFrom})` : ''}`.trim()
                    : sourceType === 'QUOTE'
                      ? `Quote ${meta.quoteNumber || ev.sourceId || ''}`.trim()
                      : sourceType === 'ORDER'
                        ? `Order ${meta.orderNumber || ev.sourceId || ''}`.trim()
                        : (sourceType ? `${sourceType}`.trim() : '-');

                const deltaNum = Number(ev.delta || 0);
                const deltaColor = deltaNum >= 0 ? '#065f46' : '#991b1b';
                const clickable = !!ev.sourceId && ['INVOICE', 'QUOTE', 'ORDER'].includes(sourceType);
                const deltaTxt = formatDeltaParts(ev.delta);

                const tdBase = {
                  padding: '10px 12px',
                  borderTop: '1px solid #f3f4f6',
                  fontSize: 12,
                  color: '#374151',
                  verticalAlign: 'top'
                };

                return (
                  <tr key={ev.id}>
                    <td style={tdBase}>{dateTxt}</td>
                    <td style={tdBase}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 9999, border: `1px solid ${badge.bd}`, background: badge.bg, color: badge.fg, fontSize: 11, fontWeight: 700 }}>
                        {typeLabel(ev.eventType)}
                      </span>
                    </td>
                    <td style={{ ...tdBase, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(cust)}>
                      {String(cust)}
                    </td>
                    <td style={{ ...tdBase, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(byUser)}>
                      {String(byUser)}
                    </td>
                    <td
                      style={{
                        ...tdBase,
                        color: clickable ? '#1e40af' : tdBase.color,
                        cursor: clickable ? 'pointer' : 'default'
                      }}
                      onClick={() => clickable && openSource(ev)}
                      title={clickable ? 'Open source' : ''}
                    >
                      <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{sourceLabel || '-'}</div>
                      {(meta.orderNumber || meta.orderId) && sourceType !== 'ORDER' && (
                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Order {meta.orderNumber || meta.orderId}</div>
                      )}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600, color: deltaColor, fontFamily: numericFont, fontVariantNumeric: 'tabular-nums' }}>
                      <span style={{ display: 'inline-block', width: '1ch', textAlign: 'center' }}>{deltaTxt.sign}</span>
                      <span style={{ textAlign: 'right' }}>{deltaTxt.value}</span>
                    </td>
                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600, color: '#111827', fontFamily: numericFont, fontVariantNumeric: 'tabular-nums' }}>
                      {ev.runningBalance == null ? '-' : Number(ev.runningBalance)}
                    </td>
                  </tr>
                );
              })}

              {loading && (
                <tr>
                  <td colSpan={7} style={{ padding: 14, fontSize: 12, color: '#6b7280' }}>
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 10 }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Showing {events.length} of {total}
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32 }}
          onClick={() => setPage((p) => p + 1)}
          disabled={loading || !canLoadMore}
        >
          {canLoadMore ? 'Load more' : 'No more'}
        </button>
      </div>

      {pdfPanel.open && (
        <div
          role="dialog"
          aria-label="PDF preview"
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(720px, 96vw)',
            background: '#fff',
            borderLeft: '1px solid #e5e7eb',
            zIndex: 1200,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: 12, borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <FiFileText />
              <div style={{ fontWeight: 700, color: '#111827', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pdfPanel.title || 'PDF'}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {pdfPanel.url && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  onClick={() => window.open(pdfPanel.url, '_blank', 'noopener')}
                  title="Open in new tab"
                >
                  <FiExternalLink /> New tab
                </button>
              )}
              {(pdfPanel.sourceType === 'INVOICE' || pdfPanel.sourceType === 'QUOTE') && pdfPanel.sourceId && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32 }}
                  onClick={() => {
                    const id = pdfPanel.sourceId;
                    setPdfPanel({ open: false, title: '', url: '', sourceType: '', sourceId: null, loading: false, error: '' });
                    if (pdfPanel.sourceType === 'INVOICE') navigate(`/invoice/${id}/edit`);
                    else navigate(`/quote/${id}/edit`);
                  }}
                  title="Open edit page"
                >
                  Open page
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, height: 32, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={() => setPdfPanel({ open: false, title: '', url: '', sourceType: '', sourceId: null, loading: false, error: '' })}
              >
                <FiX /> Close
              </button>
            </div>
          </div>

          <div style={{ flex: 1, minHeight: 0, background: '#fff' }}>
            {pdfPanel.loading && (
              <div style={{ padding: 14, fontSize: 12, color: '#6b7280' }}>Loading PDF…</div>
            )}
            {pdfPanel.error && (
              <div style={{ padding: 14, fontSize: 12, color: '#991b1b' }}>{pdfPanel.error}</div>
            )}
            {!pdfPanel.loading && !pdfPanel.error && pdfPanel.url && (
              <iframe
                title={pdfPanel.title || 'PDF'}
                src={pdfPanel.url}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

